import type { DesktopAppBranding } from "@cleo/contracts";
import { useEffect } from "react";

const STRIP_HEIGHT_PX = 28;

type Copy = NonNullable<DesktopAppBranding["copy"]>;

/** The copy marker; null in the installed Cleo, in a browser, and in Desks that predate the field. */
export function readCopy(branding: DesktopAppBranding | null | undefined): Copy | null {
  return branding?.copy ?? null;
}

export function notYourCleoStripText(copy: Copy): string {
  const what =
    copy.kind === "development"
      ? `Development copy · ${copy.subject}`
      : `Test copy · signed in as ${copy.subject}`;
  return `${what} — not your Cleo. Your Cleo is the one in Applications.`;
}

function readInjectedBranding(): DesktopAppBranding | null {
  if (typeof window === "undefined") return null;
  return window.desktopBridge?.getAppBranding?.() ?? null;
}

/**
 * A strip at the top of every window of a development or test copy of the Desk. It
 * pushes the app down so it covers nothing, and renders nothing anywhere else.
 */
export function NotYourCleoStrip() {
  const copy = readCopy(readInjectedBranding());
  const marked = copy !== null;

  useEffect(() => {
    if (!marked) return;
    const style = document.createElement("style");
    style.dataset.notYourCleo = "";
    style.textContent = `#root{padding-top:${STRIP_HEIGHT_PX}px !important;box-sizing:border-box}`;
    document.head.appendChild(style);
    return () => style.remove();
  }, [marked]);

  if (copy === null) return null;
  return (
    <div
      role="status"
      data-testid="not-your-cleo-strip"
      style={
        {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: STRIP_HEIGHT_PX,
          zIndex: 2147483647,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // Clear of the macOS window buttons at the left.
          padding: "0 84px",
          background: "#b91c1c",
          color: "#ffffff",
          font: "600 12px/1 system-ui, sans-serif",
          letterSpacing: "0.01em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          // Dragging the window by the strip keeps working.
          WebkitAppRegion: "drag",
        } as React.CSSProperties
      }
    >
      {notYourCleoStripText(copy)}
    </div>
  );
}
